/**
 * richText — the small formatting vocabulary a superadmin can use when
 * editing interface text in place.
 *
 *   **bold**        *italic*        _underline_
 *   [label](url)    line breaks     "- " at the start of a line for bullets
 *
 * Nothing here ever produces HTML from the stored string: the parser builds
 * React elements, so a stored value cannot inject markup. Links are limited
 * to http(s), mailto and site-relative paths.
 *
 * stripFormatting() gives the plain-text reading of the same string, used
 * wherever the text lands in an HTML attribute (placeholder, title, alt,
 * aria-label) or a browser dialog, where markers would show up literally.
 */

import { Fragment, type ReactNode } from 'react';

const INLINE_PATTERN = /(\*\*[^*]+\*\*|\*[^*\n]+\*|_[^_\n]+_|\[[^\]\n]+\]\([^)\s]+\))/g;

/** Only links we are willing to render. Anything else is shown as plain text. */
function safeHref(url: string): string | null {
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;
  return null;
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let n = 0;

  INLINE_PATTERN.lastIndex = 0;
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const id = `${keyPrefix}-i${n++}`;

    if (token.startsWith('**')) {
      nodes.push(<strong key={id}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={id}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('_')) {
      nodes.push(<u key={id}>{token.slice(1, -1)}</u>);
    } else {
      const split = token.indexOf('](');
      const label = token.slice(1, split);
      const href = safeHref(token.slice(split + 2, -1));
      nodes.push(
        href
          ? <a key={id} href={href} rel="noopener noreferrer">{label}</a>
          : <Fragment key={id}>{label}</Fragment>
      );
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/** True when the string uses any of the formatting markers. */
export function hasFormatting(text: string): boolean {
  if (!text) return false;
  if (text.includes('\n')) return true;
  INLINE_PATTERN.lastIndex = 0;
  return INLINE_PATTERN.test(text);
}

/**
 * Render a stored string as React nodes. Plain strings come back untouched,
 * so a label that nobody has formatted renders exactly as it did before.
 */
export function renderRichText(text: string): ReactNode {
  if (!text) return '';
  if (!hasFormatting(text)) return text;

  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let bulletStart = 0;

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    const at = bulletStart;
    bullets = [];
    blocks.push(
      <ul key={`ul-${at}`} className="artext-list">
        {items.map((item, i) => <li key={i}>{renderInline(item, `u${at}-${i}`)}</li>)}
      </ul>
    );
  };

  lines.forEach((line, i) => {
    const bullet = line.match(/^\s*-\s+(.*)$/);
    if (bullet) {
      if (bullets.length === 0) bulletStart = i;
      bullets.push(bullet[1]);
      return;
    }

    const previousWasBullet = i > 0 && /^\s*-\s+/.test(lines[i - 1] ?? '');
    flushBullets();

    // Every line after the first sits on its own line. A list already ends
    // the previous block, so it needs no extra break in front of it.
    if (i > 0 && !previousWasBullet) blocks.push(<br key={`br-${i}`} />);
    if (line.trim() !== '') {
      blocks.push(<Fragment key={`l-${i}`}>{renderInline(line, `l${i}`)}</Fragment>);
    }
  });
  flushBullets();

  return <>{blocks}</>;
}

/** The plain-text reading, for attributes and browser dialogs. */
export function stripFormatting(text: string): string {
  if (!text) return '';
  return text
    .replace(/\[([^\]\n]+)\]\([^)\s]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/_([^_\n]+)_/g, '$1')
    .replace(/^\s*-\s+/gm, '')
    .replace(/\n+/g, ' ')
    .trim();
}
